import React, { memo } from "react";
import { Loader2, Sparkles, Download, Trash2, Phone, Mail, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import SocialOutreachButtons from "@/components/SocialOutreachButtons";
import PendingSocialsBadge from "@/components/PendingSocialsBadge";
import EnrichmentBar from "@/components/EnrichmentBar";
import PhotoAvatar from "./PhotoAvatar";
import { SourceBadge, TopPerformerBadge } from "./RealtorBadges";

// Mobile card counterpart to RealtorRow. Same memoisation strategy —
// only re-renders when this specific realtor / selection / progress /
// busy state changed.
function RealtorMobileCard({
  realtor,
  selected,
  progress,
  enrichBusy,
  onToggle,
  onEnrich,
  onDownload,
  onDelete,
  onOpen,
  onPatch,
  onContactLogged,
  onSocialVerified,
  onFollowChanged,
  onBrokerageClick,
  platformOrder,
}) {
  const r = realtor;
  return (
    <li
      className="rounded-lg border border-slate-200 bg-white p-3"
      data-testid={`realtor-card-${r.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox checked={selected} onCheckedChange={() => onToggle(r.id)} />
        <PhotoAvatar realtor={r} />
        <div className="flex-1 min-w-0">
          <button onClick={() => onOpen(r)} className="text-left block w-full">
            <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
              {r.flagged_at ? (
                <Flag
                  className="h-3.5 w-3.5 text-rose-600 fill-rose-600 shrink-0"
                  aria-label="Flagged for follow-up"
                  data-testid={`flag-badge-${r.id}`}
                />
              ) : null}
              <span className="truncate">
                {r.name || `${r.first_name} ${r.last_name}`}
              </span>
              <TopPerformerBadge realtor={r} compact />
              <SourceBadge source={r.source} compact />
            </div>
            {r.title ? (
              <div className="text-xs text-slate-500 truncate">{r.title}</div>
            ) : null}
            {r.brokerage_id ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onBrokerageClick?.(r.brokerage_id); }}
                className="text-xs text-slate-500 truncate text-left hover:underline hover:text-slate-900 transition-colors"
                data-testid={`realtor-brokerage-${r.id}`}
              >
                {r.brokerage_name || "—"}
              </button>
            ) : (
              <div className="text-xs text-slate-500 truncate">{r.brokerage_name}</div>
            )}
          </button>
          <div className="text-xs mt-2 space-y-0.5">
            {r.phone ? (
              <div className="flex items-center gap-1 text-slate-700">
                <Phone className="h-3 w-3 text-slate-400" />
                {r.phone}
              </div>
            ) : null}
            {r.work_phone ? (
              <div
                className="flex items-center gap-1 text-slate-500 text-[11px]"
                data-testid={`realtor-mobile-work-phone-${r.id}`}
              >
                <Phone className="h-3 w-3 text-slate-300" />
                {r.work_phone}
              </div>
            ) : null}
            {r.email ? (
              <a
                href={`mailto:${r.email}`}
                className="flex items-center gap-1 text-slate-600 hover:text-[hsl(var(--brand-strong))]"
              >
                <Mail className="h-3 w-3 text-slate-400" />
                <span className="truncate">{r.email}</span>
              </a>
            ) : null}
            <div className="mt-2">
              <EnrichmentBar realtorId={`m-${r.id}`} progress={progress} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <SocialOutreachButtons
                realtor={r}
                onContactLogged={onContactLogged}
                onSocialVerified={onSocialVerified}
                onFollowChanged={onFollowChanged}
                platformOrder={platformOrder}
                compact
              />
              <PendingSocialsBadge realtor={r} onChange={onPatch} onFollowChanged={onFollowChanged} compact />
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-1 mt-3 justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEnrich(r)}
          disabled={enrichBusy}
          title="Enrich socials"
          className="h-8 w-8 p-0"
          data-testid={`enrich-realtor-${r.id}`}
        >
          {enrichBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDownload(r)}
          title="Download vCard"
          className="h-8 w-8 p-0"
          data-testid={`download-vcard-${r.id}`}
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(r)}
          title="Delete"
          className="h-8 w-8 p-0"
          data-testid={`delete-realtor-${r.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}

export default memo(RealtorMobileCard, (prev, next) => {
  return (
    prev.realtor === next.realtor &&
    prev.selected === next.selected &&
    prev.progress === next.progress &&
    prev.enrichBusy === next.enrichBusy &&
    prev.onToggle === next.onToggle &&
    prev.onEnrich === next.onEnrich &&
    prev.onDownload === next.onDownload &&
    prev.onDelete === next.onDelete &&
    prev.onOpen === next.onOpen &&
    prev.onPatch === next.onPatch &&
    prev.onContactLogged === next.onContactLogged &&
    prev.onSocialVerified === next.onSocialVerified &&
    prev.onFollowChanged === next.onFollowChanged &&
    prev.platformOrder === next.platformOrder
  );
});
